import { useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';

const cache = new Map(); // teamId -> { role, expiresAt }
const TTL_MS = 60_000;  // 1 minute cache

/**
 * Returns the current user's member_role for a given team.
 * Caches results to avoid hammering the API on re-renders.
 *
 * Usage:
 *   const { role, isCoach, loading } = useTeamRole(teamId);
 */
export function useTeamRole(teamId) {
  const [role, setRole]       = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!teamId) { setLoading(false); return; }

    // Return cached value if fresh
    const cached = cache.get(teamId);
    if (cached && cached.expiresAt > Date.now()) {
      setRole(cached.role);
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get(`/teams/${teamId}`);
      const r = data.myRole ?? null;
      cache.set(teamId, { role: r, expiresAt: Date.now() + TTL_MS });
      setRole(r);
    } catch {
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { fetch(); }, [fetch]);

  return {
    role,
    loading,
    isCoach:   ['coach', 'manager'].includes(role),
    isManager: role === 'manager',
    isPlayer:  role === 'player',
    isSpare:   role === 'spare',
    refresh:   fetch,
  };
}

/** Invalidate cache when membership changes (e.g. after promotion) */
export function invalidateTeamRoleCache(teamId) {
  if (teamId) cache.delete(teamId);
  else cache.clear();
}
